import { Button, Spinner } from "@design";
import { useQuery } from "@tanstack/react-query";
import { Eye } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { getCompanyProfileOptions } from "@/api";
import { Text } from "@/components/Text";
import { AdminPage } from "../AdminPage";
import { useAdminCanEdit } from "../useAdminCanEdit";
import { CompanyProfileForm } from "./CompanyProfileForm";
import { CompanyProfileReadOnly } from "./CompanyProfileReadOnly";
import { ContextPreviewModal } from "./ContextPreviewModal";

/** Admin → Company profile: the org context prepended to every AI system prompt. */
export function CompanyProfilePage() {
  const { t } = useTranslation("adminCompany");
  const canEdit = useAdminCanEdit();
  const profile = useQuery(getCompanyProfileOptions());
  const [previewOpen, setPreviewOpen] = useState(false);
  const existing = profile.data?.data ?? null;

  let body: React.ReactNode;
  if (profile.isPending) body = <Spinner />;
  else if (profile.isError)
    body = (
      <Text tone="error" role="alert">
        {t("loadFailed")}
      </Text>
    );
  else if (canEdit) body = <CompanyProfileForm key={existing?.updated_at ?? "new"} profile={existing} />;
  else body = <CompanyProfileReadOnly profile={existing} />;

  return (
    <AdminPage
      title={t("title")}
      subtitle={t("subtitle")}
      actions={
        <Button
          variant="secondary"
          size="sm"
          leadingIcon={<Eye size={16} aria-hidden />}
          disabled={!existing}
          onClick={() => setPreviewOpen(true)}
        >
          {t("preview.open")}
        </Button>
      }
    >
      <Text variant="body-sm" tone="secondary">
        {t("intro")}
      </Text>
      {body}
      <ContextPreviewModal open={previewOpen} onClose={() => setPreviewOpen(false)} />
    </AdminPage>
  );
}
